// W36: thin wrapper over the generated flows-llm client.
//
// Types come from `./generated/flows-llm` (regenerate with
// `pnpm --filter @repletoryreach/sdk-api generate flows-llm`); the request path
// stays on the house core so the envelope, cursor pagination, Idempotency-Key,
// token refresh and ApiError mapping behave exactly as before.

import { http } from './_core/request.js';
import type { Envelope, EnvelopeWithMeta, Meta } from './_core/envelope.js';
import type { Page, PageOpts } from './_core/pagination.js';
import { buildPageQuery } from './_core/pagination.js';
import type * as gen from './generated/flows-llm/index.js';

// ── Flows (generated) ──────────────────────────────────────────────────────

/**
 * A flow row. Every field comes from the generated `FlowDto`: including
 * `created_by`, which the service has always returned and this module never
 * declared: except `description`, which is pinned below.
 *
 * `definition` is the generated `JsonElement`, i.e. `unknown`: the service
 * hands back whatever the stored `definition` column deserialised to. That is
 * normally the flow object, but it can be an array, and it is the **raw
 * string** when the stored JSON fails to parse. Narrow it (the tenant console
 * uses an `isPlainObject` guard) before reading properties off it.
 *
 * A Web API step's sign-in secrets (`token`, `password`, `key_value`,
 * `header_value`, `client_secret`) are write-only: a create or update stores
 * them encrypted outside the definition, and the definition read back names
 * the saved ones in `auth.saved`. Leave a secret out to keep the saved one,
 * send a value to replace it. A copied flow carries none.
 */
export type Flow = gen.FlowDto & {
  /**
   * ⚠ DRIFT; there is no `description` column on `flows.flows` and no request
   * field accepts one, so this is **always absent**. Declared (optional) so the
   * consoles' `flow.description ?? '–'` render paths keep compiling.
   */
  description?: string | null;
  /** The bot the flow belongs to. Only that bot runs it (21 September 2026). */
  bot_id?: string | null;
};

/**
 * One knowledge-base passage the answer was grounded on. W20: returned on flow
 * runs and on `searchKb`; empty when the run's LLM node had no `kb_ids` or
 * nothing matched. `score` is a cosine similarity in [0, 1] (a flat 0.5 for
 * substring-fallback hits).
 */
export type LlmCitation = gen.CitationDto;

/**
 * One flow run (`GET /v1/flows/{id}/runs`). The generated `FlowRunDto` also
 * carries `trigger_text`, `final_reply` and `handed_off_to`, which the service
 * has always returned and this module never declared.
 *
 * `confidence` is a C# `float`: single precision, so expect values like
 * `0.8199999928474426` rather than the `0.82` that was written.
 */
/**
 * One node a run executed. `outcome` is one of `sent`, `asked`, `answered`,
 * `retry`, `invalid`, `set`, `yes`, `no`, `http_ok`, `http_error`, `llm`,
 * `contact`, `handoff`, `delay`, `end`, `skipped` or `error`.
 */
export type FlowStep = gen.FlowStepDto & {
  /** The API call this step made, in the API call log for 48 hours (19 October 2026). */
  call?: string | null;
};

/** A message the flow sent during a test turn, as the customer would see it. */
export type FlowTestMessage = gen.FlowTestMessageDto;
export type FlowTestOption = gen.FlowTestOptionDto;

/**
 * What happened in one turn of the test chat: the messages the flow sent,
 * where it now stands (`state` is `waiting_input` when it asked something,
 * `done` when it ended), and everything it has collected so far.
 */
export type FlowTestTurn = gen.FlowTestTurnDto;
export type AssistantMessage = gen.AssistantMessage;
export type AssistantNeed = gen.AssistantNeed;
/** `format: 'html'` (14 October 2026): the reply comes as simple HTML (p, strong, em, ul, ol, li, br) instead of plain words. */
/** `model` (19 October 2026): a `code` from `assistModels()`; omitted, the standard model. */
export type FlowAssistRequest = gen.FlowAssistRequest & { format?: 'html'; model?: string };

/**
 * A model Build with AI can use (19 October 2026). `cost_factor` is roughly how many times the standard model's
 * credit one message costs, from the business's own prices; null when it cannot be said.
 */
export interface AssistantModel {
  code: string;
  name: string;
  description?: string | null;
  is_default: boolean;
  cost_factor?: number | null;
}
export type FlowAssistResponse = gen.FlowAssistResponse;
export type BotProfile = gen.BotProfileDto;
/**
 * `POST /v1/kb/ask`. `action` (16 October 2026) is what the assistant would do instead of answering, when the
 * bot's business switched bookings or tickets on: start a booking, help with one the customer has, or log a
 * request for the team. `answer` is null then.
 */
export type KbAskResult = gen.KbAskResultDto & { action?: AssistantAction | null };
/** What a chatbot may hand a customer over to (16 October 2026). */
export type AssistantAction = 'book' | 'manage_booking' | 'new_ticket';
export type HttpProbeRequest = gen.HttpProbeRequest;
export type HttpProbePath = gen.HttpProbePathDto;
/**
 * What "Test this request" returns. `reply_by_ai` (1 October 2026) is true when the step lets the AI word its
 * reply: the test does not call the AI, so `reply` is null and the wording shows in the test chat.
 */
export type HttpProbeResult = gen.HttpProbeResultDto & { reply_by_ai?: boolean };
/** `offered_flow_id` (1 October 2026): the flow that went out as a button with the question; a tap resolves it as `sent_to_flow`. */
export type BotQuestion = gen.BotQuestionDto & { offered_flow_id?: string | null };

/**
 * What a flow is for, on its trigger (`definition.trigger.intent`). A customer who asks for it in their own
 * words is sent into the flow (`jump`) or offered it as a button (`offer`); `off` switches it off.
 */
export interface FlowIntent {
  description?: string;
  examples?: string[];
  mode?: 'jump' | 'offer' | 'off';
  /** At most 20 characters; the flow's name when omitted. */
  button_label?: string;
}

/**
 * Why a run began. Null on runs from before 1 October 2026. `post_resolution` (stamped from 18 October 2026) is the
 * flow that runs after the team resolves a chat; a question it asks is closed without a check-in when nobody answers.
 * `chosen` (18 October 2026): the message met more than one flow's keyword, or one only closely, and the assistant
 * chose this flow; the run's first step says why. `choice`: the customer picked it when asked which they meant.
 */
export type RunStartedBy = 'keyword' | 'intent' | 'first_contact' | 'greeting' | 'default' | 'offer' | 'switch' | 'api' | 'test' | 'post_resolution' | 'chosen' | 'choice';

export interface IntentTestFlow {
  id: string;
  name: string;
  mode?: 'jump' | 'offer' | null;
  button_label?: string | null;
  /** 0 to 1: how close the message came to what the flow is for. */
  score?: number | null;
}

/**
 * `POST /v1/flows/intent-test`: what a customer's message would start or offer. Nothing runs and nothing is charged.
 * `choose` (18 October 2026): the message met more than one flow's keyword (`several_keywords`) or one only closely
 * (`close_keyword`), and the flows in `choices` would be chosen between, as `choice_mode` says.
 */
export interface IntentTestResult {
  action: 'jump' | 'offer' | 'keyword' | 'choose' | 'none';
  reason: 'clear' | 'likely' | 'question' | 'offer_only' | 'close_call' | 'keyword' | 'several_keywords' | 'close_keyword' | 'no_match' | 'no_flows'
    | 'greeting' | 'small_talk' | 'too_short' | 'too_long' | 'unavailable' | (string & {});
  flow?: IntentTestFlow | null;
  offers: IntentTestFlow[];
  candidates: IntentTestFlow[];
  thresholds: { jump_at: number; offer_at: number; switch_at: number; margin: number; switch_mid_flow: boolean };
  /** `bedrock` or `dev-hash`: scores from one are not comparable with the other. */
  embedder: string;
  /** For `choose`: the flows chosen between, with the words on their buttons. */
  choices?: IntentTestFlow[] | null;
  /** For `choose`: the chatbot's setting (see `BotFlowChoice`). */
  choice_mode?: BotFlowChoice['mode'] | null;
}

/** `POST /v1/flows/intent-suggestions` (18 October 2026): more ways customers might ask for a flow. Charged as AI tokens. */
export interface IntentSuggestions {
  suggestions: string[];
  in_tokens: number;
  out_tokens: number;
}

/**
 * One clash of a flow's keyword with another active flow of the same chatbot (18 October 2026): `same` once tidied,
 * `overlap` when one sits inside the other ("tour" in "book a tour"), `close` when one would be a close match for the
 * other. `has_intent`: the other flow says what it is for, which the assistant uses to choose.
 */
export interface KeywordOverlap {
  keyword: string;
  other_keyword: string;
  flow_id: string;
  flow_name: string;
  kind: 'same' | 'overlap' | 'close';
  has_intent: boolean;
}
export type BotQuestionSummary = gen.BotQuestionSummaryDto;
export type ResolveBotQuestionRequest = gen.ResolveBotQuestionRequest;
export type BotQuestionResolved = gen.BotQuestionResolvedDto;
export type KbAskSnippet = gen.KbAskSnippetDto;
/** The behaviour may carry the take-over and hand-back lines (`handover`, 4 October 2026). */
export type UpdateBotProfileRequest = Omit<gen.UpdateBotProfileRequest, 'behaviour'> & {
  behaviour?: (Omit<gen.BotBehaviourDto, 'handover'> & {
    handover?: Pick<BotHandover, 'joined_message' | 'back_message'> | null;
    /** Omitted or null keeps what the bot has; a missing switch inside keeps that one. */
    abilities?: Partial<BotAbilities> | null;
    /** Omitted or null keeps what the bot has; a missing field inside keeps that one, and a blank line is the default. */
    idle?: Partial<Pick<BotIdle, 'enabled' | 'nudge_after_minutes' | 'close_after_minutes' | 'nudge_message' | 'close_message'>> | null;
    /** Omitted or null keeps what the bot has; a blank question is the default. */
    flow_choice?: Partial<Pick<BotFlowChoice, 'mode' | 'question'>> | null;
    /** Voice notes, photos and documents (27 October 2026). Omitted or null keeps what the bot has; a missing switch keeps that one. */
    media?: Partial<BotMedia> | null;
    /**
     * The languages the chatbot speaks (1 November 2026). Omitted or null keeps what the bot has; a missing field
     * inside keeps that one. `main` is read-only: send the bot's `language` to change it.
     */
    languages?: Partial<Pick<BotLanguages, 'offered' | 'mode' | 'change_words'>> | null;
  }) | null;
};
/**
 * How the bot behaves beyond answering (15 September 2026): replies to small
 * talk ("thank you", "k", "bye") sent without looking anything up; language it
 * does not accept (the default list plus `extra_words`, less `allowed_words`),
 * answered with `reply` and flagged, with `action` `warn` or `handover`; and the
 * notice a customer hears while a person has the conversation. Always complete
 * on read; on update, a blank reply falls back to the default.
 */
export type BotBehaviour = gen.BotBehaviourDto & {
  /** What the customer is told on a take-over and a hand-back (4 October 2026). */
  handover?: BotHandover | null;
  /** What the bot may do for a customer besides answering (16 October 2026). Both off until the business turns them on. */
  abilities?: BotAbilities | null;
  /** When the customer stops answering a question (18 October 2026). On by default. */
  idle?: BotIdle | null;
  /** When more than one flow matches a message (18 October 2026). The assistant chooses by default. */
  flow_choice?: BotFlowChoice | null;
  /**
   * What the chatbot reads when a customer sends a file instead of words (27 October 2026): voice notes transcribed,
   * photos described, documents read, each charged as AI use. All on by default; leaving it out keeps what the bot has.
   */
  media?: BotMedia | null;
  /** The languages it speaks from its one knowledge base, and how a customer gets theirs (1 November 2026). */
  languages?: BotLanguages | null;
};

/**
 * How a customer gets their language (1 November 2026). `ask`: a customer with no language yet is offered a menu of
 * the offered languages, each in its own name. `follow`: the language they write in becomes theirs, and writing in
 * another offered language later brings a "Switch to isiZulu?" question first. `contact`: no menu and no detection;
 * the contact's own language (set by staff, an import or a flow) is used, and the main language otherwise.
 */
export type BotLanguageMode = 'ask' | 'follow' | 'contact';

/**
 * The languages a chatbot speaks (1 November 2026). `offered` are catalogue codes ({@link listLanguages}) it speaks
 * besides its main language; empty means the main language only, which is how every chatbot worked before. At most
 * 12 offered languages and 20 change words. `change_words` are the business's own words that bring the language menu
 * back; "language", "change language" and each language's own word ("taal", "ulimi", "lugha") are built in.
 *
 * `main` is read-only: the bot's `language` resolved to a catalogue code (`en` when blank or unknown). A customer
 * who writes in another language has their question put into the main language by one small AI call, so the one
 * knowledge base can be searched; it is charged as AI tokens at plan rates, as "AI translation" in the ledger.
 */
export interface BotLanguages {
  offered: string[];
  mode: BotLanguageMode;
  change_words: string[];
  readonly main?: string | null;
}

/** Where a language is spoken, which is how the consoles group the list. */
export type LanguageRegion = 'south_africa' | 'africa' | 'widely_used';

/**
 * How well the AI writes a language. `review`: answers are fine, but a native speaker should read the chatbot's
 * fixed wording before a business relies on it.
 */
export type LanguageQuality = 'strong' | 'good' | 'review';

/** A language a chatbot can speak: `GET /v1/languages` (1 November 2026). */
export interface Language {
  /** The catalogue code a bot's `language`, its `languages.offered` and a contact's `locale` use (`en`, `zu`, `nso`). */
  code: string;
  /** The English name ("Zulu"). */
  name: string;
  /** The language's own name ("isiZulu"), as customers see it on the language menu. Never translated. */
  native_name: string;
  region: LanguageRegion | string;
  sort_order: number;
  quality: LanguageQuality | string;
  /** Meta's WhatsApp template language codes for it; empty when templates cannot be in it (the chatbot can still chat in it). */
  meta_codes: string[];
  /** Voice notes in it can be turned into words. */
  speech: boolean;
}

/**
 * The languages the platform offers, in the order to show them. Only the ones switched on; any signed-in user. The
 * one list for a chatbot's languages and a contact's language.
 */
export function listLanguages(): Promise<Envelope<Language[]>> {
  return http.get<Envelope<Language[]>>('/v1/languages');
}

// ── A flow's translations (1 November 2026) ──────────────────────────────────
//
// A flow's fixed wording (messages, questions, button and list labels) is translated automatically the first time a
// customer is answered in another language its chatbot offers, and the business can correct it. Owners and admins
// write; the translations follow the saved flow.

/**
 * One piece of a flow's wording in one language. `path` names it within its step (`body`, `options.0.title`,
 * `list.options.2.description`). `source`: `machine`, `edited` (the business's own) or `missing` (translated the first
 * time a customer needs it, or now with {@link translateFlow}). `stale`: an edited translation whose original has
 * changed since. `max_length`: a button (20), a list row (24), a row description (72) or a heading.
 */
export interface FlowTranslationItem {
  node_id: string;
  node_name?: string | null;
  path: string;
  source_text: string;
  text?: string | null;
  source: 'machine' | 'edited' | 'missing' | string;
  reviewed: boolean;
  stale: boolean;
  max_length?: number | null;
}

/**
 * `GET /v1/flows/{id}/translations`. `languages` are those the flow's chatbot offers besides its `main` one (empty: the
 * chatbot speaks one language); `language` is the one `items` are in (null when there are none); `quality` is that
 * language's (`review`: have a native speaker read it).
 */
export interface FlowTranslations {
  flow_id: string;
  main: string;
  languages: string[];
  language?: string | null;
  quality?: LanguageQuality | string | null;
  items: FlowTranslationItem[];
}

/**
 * `PUT /v1/flows/{id}/translations`. A blank `text` removes the business's own version (the next customer gets a fresh
 * translation). Refused with 400 (`error.field` `text`) when a `{{...}}` part of the original is dropped or it is too
 * long, and (`language`) when the chatbot does not offer the language.
 */
export interface UpdateFlowTranslationRequest {
  language: string;
  node_id: string;
  path: string;
  text?: string;
  reviewed?: boolean;
}

export interface TranslateFlowResult {
  written: number;
}

/** A flow's wording in one language its chatbot offers; `language` omitted gives the first. */
export function listFlowTranslations(flowId: string, language?: string): Promise<Envelope<FlowTranslations>> {
  return http.get<Envelope<FlowTranslations>>(`/v1/flows/${encodeURIComponent(flowId)}/translations`, language ? { query: { language } } : undefined);
}

/** Correct one piece of a flow's wording in one language, or mark it as read. Answers with the piece as saved. */
export function updateFlowTranslation(flowId: string, req: UpdateFlowTranslationRequest): Promise<Envelope<FlowTranslationItem>> {
  return http.put<Envelope<FlowTranslationItem>>(`/v1/flows/${encodeURIComponent(flowId)}/translations`, { body: req });
}

/**
 * Translate the wording a language lacks now, charged to the business as AI translation at its plan's rates. `all`
 * remakes every machine translation as well; the business's own versions are kept.
 */
export function translateFlow(flowId: string, req: { language: string; all?: boolean }): Promise<Envelope<TranslateFlowResult>> {
  return http.post<Envelope<TranslateFlowResult>>(`/v1/flows/${encodeURIComponent(flowId)}/translations/translate`, { body: req });
}

/**
 * When a message meets more than one flow's keyword, or meets one only closely, as with a spelling mistake (18 October
 * 2026). `assistant` (the default): the assistant chooses with the conversation, charged as AI tokens, and asks the
 * customer with a button per flow when it cannot tell. `ask`: always ask, no AI. `question` is the business's own
 * wording for that question; null or blank is the default in the bot's language, given in `default_question` on read.
 */
export interface BotFlowChoice {
  mode: 'assistant' | 'ask';
  question: string | null;
  default_question?: string | null;
}

/**
 * When the customer stops answering a question (18 October 2026). `nudge_after_minutes` after the bot last asked
 * something it asks whether they are still there, repeating the question with its buttons; `close_after_minutes`
 * after that it sends the closing line and ends the chat, so their next message starts afresh. Minutes are 1 to 720.
 * A null or blank line is the default in the bot's language, given in `default_nudge_message` and
 * `default_close_message` on read (ignored on update). `{{contact.name ?? there}}` and `{{bot.name}}` work in both.
 * Whatever the setting, a chat left waiting a day is closed without a word, and so is the question after a person
 * resolved the conversation.
 */
export interface BotIdle {
  enabled: boolean;
  nudge_after_minutes: number;
  close_after_minutes: number;
  nudge_message: string | null;
  close_message: string | null;
  default_nudge_message?: string | null;
  default_close_message?: string | null;
}

/**
 * The bot's bookings and tickets switches (16 October 2026). `bookings`: it knows the services, prices, opening
 * hours and today's date, tells a customer about their own bookings, and books, moves or cancels through the
 * booking steps. `tickets`: it tells a customer where their requests stand and logs a new one for the team.
 */
export interface BotAbilities {
  bookings: boolean;
  tickets: boolean;
}

/** Voice notes, photos and documents the chatbot reads (27 October 2026). */
export interface BotMedia {
  voice_notes: boolean;
  photos: boolean;
  documents: boolean;
}

/**
 * The take-over and hand-back lines (4 October 2026). Null or blank is the default in the bot's language,
 * given in `default_joined_message` and `default_back_message` on read (ignored on update). `{{person}}` is
 * the team member's first name and `{{bot}}` the bot's name.
 */
export interface BotHandover {
  joined_message: string | null;
  back_message: string | null;
  default_joined_message?: string | null;
  default_back_message?: string | null;
}
export type BotSmallTalk = gen.BotSmallTalkDto;
export type BotModeration = gen.BotModerationDto;
export type BotPauseNotice = gen.BotPauseNoticeDto;
export type NotAcceptedWords = gen.NotAcceptedWordsDto;
/** What the bot does after answering unacceptable language. */
export type ModerationAction = 'warn' | 'handover';
export type WebSite = gen.WebSiteDto;
export type CreateWebSiteRequest = gen.CreateWebSiteRequest;

/**
 * A website chat's name, greeting and allowed websites, changed in place
 * (`PUT /v1/web-sites/{id}`). Written by hand until the generated types are
 * refreshed. The site key does not change, so a snippet already on a website
 * keeps working.
 */
export interface UpdateWebSiteRequest {
  name: string;
  /** The first message a visitor sees, up to 500 characters; empty uses the chatbot's own opening line. */
  greeting?: string | null;
  /** Websites that may show the chat, as https://www.example.co.za; empty means any website. */
  allowed_origins?: string[] | null;
}

/**
 * A run is a conversation session: it stands on a node (`current_node_id`)
 * while `state` is `waiting_input` or `waiting_delay`, carries what it has
 * collected in `variables`, and logs every node it executed in `steps`.
 * `channel` is `whatsapp`, or `test` for the builder's test chat.
 */
export type FlowRun = gen.FlowRunDto & {
  /**
   * @deprecated W46: the wire field is, and always was, `status`. This alias
   * is **not** sent by the service; it is left declared (optional) only so a
   * call site pinned to the old name still compiles. Read `status`.
   */
  status_code?: string;
  /** ⚠ DRIFT; the run projection does not return `flow_id` (it is already the
   *  route parameter), so this is always absent. */
  flow_id?: string;
  /**
   * ⚠ Retired. `error_code` was never projected and the console rendered a
   * permanently empty column for it. The run's failure reason is now on
   * `error`, which `services/flows-llm` projects from `FlowRun.Error` (a
   * column that existed all along). Kept as an optional so an older call site
   * still compiles; read `error`.
   */
  error_code?: string | null;
  /** Why the run began (1 October 2026). */
  started_by?: RunStartedBy | null;
  /** How closely the message matched what the flow is for, when that started it. */
  intent_score?: number | null;
};

// ── Knowledge base (generated) ─────────────────────────────────────────────

/** `GET /v1/kb/documents` - the list projection. No `embedding_provider`. */
/** `live_source_id` (1 October 2026) is set when the document is the stored copy of a live source. */
export type KbDocumentListItem = gen.KbDocumentListItem & {
  live_source_id?: string | null;
  /** Set when the document is a page of a website in the knowledge base (3 October 2026). */
  website_id?: string | null;
  source_url?: string | null;
};
/** `POST /v1/kb/documents`: the ingest result. */
export type KbDocumentDetail = gen.KbDocumentDto;
/** `POST /v1/kb/documents/from-media` - adds `source_media_id`, `source_mime` and `source_name`. */
export type KbDocumentFromMedia = gen.KbDocumentFromMediaDto;
/** `GET /v1/kb/documents/{id}` - the list row plus `body`, the text the assistant reads. */
export type KbDocumentFull = gen.KbDocumentDetailDto;

/**
 * The module's historical `KbDocument`: the widest of the three shapes the
 * service actually returns, so one type still serves `listKbDocuments`,
 * `createKbDocument` and `ingestKbFromMedia`. Use the three aliases above when
 * you want the exact per-endpoint shape.
 */
export type KbDocument = Omit<gen.KbDocumentDto, 'embedding_model' | 'embedding_provider'> & {
  /** `null` on list rows that were never embedded; a model id everywhere else. */
  embedding_model?: string | null;
  /**
   * ⚠ DRIFT: `GET /v1/kb/documents` omits `embedding_provider` (create and
   * from-media both return it), so it is absent on list rows only.
   */
  embedding_provider?: string | null;
  /**
   * The media object the text was read from, still in the library, so the
   * file can be opened. Null (or absent) on a pasted document.
   */
  source_media_id?: string | null;
  source_mime?: string | null;
  source_name?: string | null;
  /**
   * `queued` and `indexing` while the index worker has it, `indexed` once
   * searchable, `failed` with {@link KbDocument.error} saying why. A document
   * added through the single-file routes says `ingested`, which also means
   * indexed. Typed as `string` for forward compatibility; see
   * {@link KbDocumentStatus} and {@link isKbDocumentPending}.
   */
  status: string;
  /** Why indexing failed, in words the customer can act on. */
  error?: string | null;
  /** How many times the index worker has tried this document. */
  attempts?: number;
  /** When the passages were written; null until then. */
  indexed_at?: string | null;
  /** Set when the document is the stored copy of a live source (1 October 2026). */
  live_source_id?: string | null;
  /** Set when the document is a page of a website (3 October 2026); its address is `source_url`. */
  website_id?: string | null;
  source_url?: string | null;
};

export type KbDocumentStatus = 'queued' | 'indexing' | 'indexed' | 'failed' | 'ingested';

/** True while the index worker still has the document: poll until this is false. */
export function isKbDocumentPending(d: Pick<KbDocument, 'status'>): boolean {
  return d.status === 'queued' || d.status === 'indexing';
}

/** One already-uploaded media library file to add to the knowledge base. */
export interface KbBatchItem {
  media_id: string;
  /** Defaults to the file's original name. */
  name?: string;
}

/** A file the batch did not queue. `code` is stable; `message` is English, for logs. */
export interface KbBatchRefusal {
  media_id: string;
  name: string | null;
  code: 'PLAN_LIMIT_REACHED' | 'ALREADY_ADDED' | 'NO_TEXT' | 'MEDIA_NOT_FOUND' | 'MEDIA_FETCH_FAILED' | (string & {});
  message: string;
}

export interface KbBatchResult {
  /** Queued for indexing, in the order given. They index one at a time. */
  accepted: KbDocument[];
  refused: KbBatchRefusal[];
}

export type CreateKbDocumentRequest = gen.CreateKbDocRequest;
export type KbSnippet = gen.KbSnippetDto;

export type KbSearchResult = Omit<gen.KbSearchResultDto, 'retriever'> & {
  /**
   * Which retriever answered. Pinned to the three values the service emits
   * the OpenAPI document types it as an open `string`.
   */
  retriever: 'vector' | 'substring' | 'none';
};

/** `failed` counts documents whose embedding call failed; they keep substring retrieval. */
export type KbReindexResult = gen.KbReindexResultDto;

/** W20: which LLM this deployment is actually talking to. Platform-admin only. */
export type LlmProvider = Omit<gen.LlmProviderDto, 'source'> & {
  /**
   * `registry` (Settings → Integrations), `config` (`Bedrock:*`), or `none`
   * (stub). Pinned: the OpenAPI document types it as an open `string`.
   */
  source: 'registry' | 'config' | 'none';
};

// ── Requests (generated) ───────────────────────────────────────────────────

/**
 * `description` is accepted by the SDK for backwards compatibility with the
 * tenant console's flow drawer, but `POST /v1/flows` does not read it: there
 * is no column behind it. See the drift note on {@link Flow}.
 */
export type CreateFlowRequest = gen.CreateFlowRequest & {
  description?: string;
  /** The bot the flow belongs to; the default bot when omitted. */
  bot_id?: string;
};

/** `POST /v1/flows/{id}/test`: start a test chat. Every field is optional. */
export type StartTestRequest = gen.StartTestRequest;
/** `POST /v1/flows/{id}/test/{session}`: the pretend customer's next message. */
export type TestTurnRequest = gen.TestTurnRequest;
/** `POST /v1/flows/{id}/trigger`: start a run for a contact from your own system. */
export type TriggerFlowRequest = gen.TriggerFlowRequest;
export type TriggerFlowResult = gen.TriggerFlowResultDto;

/** Same story as {@link CreateFlowRequest}: `description` is sent and ignored. */
export type UpdateFlowRequest = gen.UpdateFlowRequest & {
  description?: string;
  /** Move the flow to this bot. Omitted, it stays where it is. */
  bot_id?: string;
};

// ── Operations ─────────────────────────────────────────────────────────────

/**
 * The plan's `flows` allowance as `GET /v1/flows` reports it: the same shape as
 * {@link BotAllowance}. `allowance` is absent when unlimited: read `unlimited`.
 *
 * `current` counts only the flows that count (30 October 2026). Entry-point
 * flows do not: a flow whose trigger is `first_contact`, `greeting`, `default`
 * or `post_resolution` (`always`, and `message` with no keywords, count as
 * `default`) is free when it is the newest of its kind for its bot and
 * channels. Extra copies count.
 */
export type FlowAllowance = BotAllowance;

/** `GET /v1/flows` metadata. */
export interface FlowListMeta extends Meta {
  count?: number;
  allowance?: FlowAllowance;
  /** How many entry-point flows were left out of `allowance.current` (30 October 2026). */
  entry_free?: number;
}

/**
 * `botId` narrows the list to one bot's flows; `meta.allowance` still covers the
 * whole organisation, and counts only the flows that count: entry-point flows
 * are free (see {@link FlowAllowance}) and `meta.entry_free` says how many were
 * left out. Creating a flow, or saving one so that it starts counting (a
 * greeting flow changed to a keyword flow on a full plan, or moved to a bot
 * that already has that entry point), fails with 409 `PLAN_LIMIT_REACHED`; see
 * `billing.isPlanLimitReached`.
 */
export function listFlows(opts: { botId?: string } = {}): Promise<EnvelopeWithMeta<Flow[], FlowListMeta>> {
  return http.get<EnvelopeWithMeta<Flow[], FlowListMeta>>('/v1/flows', opts.botId ? { query: { bot_id: opts.botId } } : undefined);
}

export function getFlow(id: string): Promise<Envelope<Flow>> {
  return http.get<Envelope<Flow>>(`/v1/flows/${encodeURIComponent(id)}`);
}

export function createFlow(req: CreateFlowRequest): Promise<Envelope<Flow>> {
  return http.post<Envelope<Flow>>('/v1/flows', { body: req });
}

export function updateFlow(id: string, req: UpdateFlowRequest): Promise<Envelope<Flow>> {
  return http.put<Envelope<Flow>>(`/v1/flows/${encodeURIComponent(id)}`, { body: req });
}

export function deleteFlow(id: string): Promise<void> {
  return http.delete<void>(`/v1/flows/${encodeURIComponent(id)}`);
}

/**
 * Runs of one flow, newest first. W46: `limit` and `cursor` are bound and
 * honoured and `meta` carries `total` + `next_cursor`, so `paginate()` walks
 * every run; before W46 the handler took no parameters and hard-coded
 * `Take(50)`.
 */
export interface FlowRunOpts extends PageOpts {
  /** Run status CODE, e.g. `failed`. "Failed only" is the filter this exists for. */
  status?: string;
  /** Row offset, for a numbered pager. Takes precedence over `cursor`. */
  offset?: number;
  /** `test` lists the builder's test chats, which the default list hides. */
  channel?: string;
}

export function listFlowRuns(flowId: string, opts?: FlowRunOpts): Promise<Page<FlowRun>> {
  const query: Record<string, string | number | undefined> = { ...buildPageQuery(opts) };
  if (opts?.status) query.status = opts.status;
  if (opts?.offset !== undefined) query.offset = opts.offset;
  if (opts?.channel) query.channel = opts.channel;
  return http.get<Page<FlowRun>>(`/v1/flows/${encodeURIComponent(flowId)}/runs`, { query });
}

/** One run, with its steps and variables. */
export function getFlowRun(flowId: string, runId: string): Promise<Envelope<FlowRun>> {
  return http.get<Envelope<FlowRun>>(`/v1/flows/${encodeURIComponent(flowId)}/runs/${encodeURIComponent(runId)}`);
}

// ── Test chat ──────────────────────────────────────────────────────────────
//
// A flow can be tried without a phone. Nothing leaves the platform and nothing
// is billed: every message the flow would have sent comes back in the turn.

/**
 * Start a test chat. Pass `definition` to test the builder's unsaved draft;
 * `message` is what the pretend customer says first (defaults to the
 * trigger's first keyword); `variables` seeds the context, e.g.
 * `{ contact: { name: 'Mary' } }`.
 */
/**
 * Build or change a flow by chatting. Send the conversation so far (oldest
 * first) and, when editing, the flow as it stands; the reply carries plain
 * words and, when the assistant knows enough, a whole definition for the
 * canvas, plus its changes, questions, what it needs from outside the flow
 * (a document, a template, a number, an API) and suggested next steps.
 */
/**
 * How long Build with AI may take before the console gives up: a whole flow can take most of a minute to write, and a
 * stronger model (19 October 2026) writes more slowly. Under the gateway's two minutes for this route.
 */
export const ASSIST_TIMEOUT_MS = 110_000;

/**
 * One turn of Build with AI. Waits up to {@link ASSIST_TIMEOUT_MS} (19 October 2026): the usual 20 seconds cut off
 * bigger flows half written ("Request timed out after 20000ms" at Acts House of Education), and the gateway allows
 * this route two minutes.
 */
export function assist(req: FlowAssistRequest): Promise<Envelope<FlowAssistResponse>> {
  return http.post<Envelope<FlowAssistResponse>>('/v1/flows/assistant', { body: req, timeoutMs: ASSIST_TIMEOUT_MS });
}

/** The models Build with AI can use (19 October 2026): the standard one first, then any stronger one switched on. */
export function assistModels(): Promise<Envelope<AssistantModel[]>> {
  return http.get<Envelope<AssistantModel[]>>('/v1/flows/assistant/models');
}

/** One turn of Build with AI, kept 30 days so the assistant can be tuned from it. */
export interface AssistantTranscript {
  id: string;
  organisation_id: string;
  flow_id: string | null;
  /** The conversation as the assistant was sent it, newest last. */
  conversation: string;
  reply: string;
  /** It put a flow on the canvas. */
  proposed_flow: boolean;
  /** What was wrong with the flow it proposed. */
  problems: string[];
  model: string | null;
  in_tokens: number;
  out_tokens: number;
  created_at: string;
}

/** Build with AI conversations of the last 30 days, newest first. Platform admins only. */
export function listAssistantTranscripts(opts: { orgId?: string; limit?: number } = {}): Promise<Envelope<AssistantTranscript[]>> {
  return http.get<Envelope<AssistantTranscript[]>>('/v1/flows/assistant/transcripts', {
    query: { org_id: opts.orgId, limit: opts.limit }
  });
}

/**
 * Questions customers asked the AI. `unanswered` keeps only the misses; `open` (default true)
 * keeps only those nobody has resolved yet. This is the miss-training queue.
 */
export function listBotQuestions(opts: { unanswered?: boolean; open?: boolean; limit?: number } = {}): Promise<Envelope<BotQuestion[]>> {
  const q = new URLSearchParams();
  if (opts.unanswered !== undefined) q.set('unanswered', String(opts.unanswered));
  if (opts.open !== undefined) q.set('open', String(opts.open));
  if (opts.limit !== undefined) q.set('limit', String(opts.limit));
  const qs = q.toString();
  return http.get<Envelope<BotQuestion[]>>(`/v1/bot/questions${qs ? `?${qs}` : ''}`);
}

/** Answered and unanswered questions over the last seven days, and how many misses are still open. */
export function getBotQuestionSummary(): Promise<Envelope<BotQuestionSummary>> {
  return http.get<Envelope<BotQuestionSummary>>('/v1/bot/questions/summary');
}

/**
 * Close a question. `answer_added` with an `answer` puts a question-and-answer entry in the
 * knowledge base so the AI answers it next time; the others just mark it handled.
 */
export function resolveBotQuestion(id: string, req: ResolveBotQuestionRequest): Promise<Envelope<BotQuestionResolved>> {
  return http.post<Envelope<BotQuestionResolved>>(`/v1/bot/questions/${encodeURIComponent(id)}/resolve`, { body: req });
}

/** The bot's persona for this tenant: the name it introduces itself with, what the business does, its tone. Defaults when never set. */
export function getBotProfile(): Promise<Envelope<BotProfile>> {
  return http.get<Envelope<BotProfile>>('/v1/bot-profile');
}

export function updateBotProfile(req: UpdateBotProfileRequest): Promise<Envelope<BotProfile>> {
  return http.put<Envelope<BotProfile>>('/v1/bot-profile', { body: req });
}

/** The default list of words the bot does not accept, which every organisation starts with. */
export function getNotAcceptedWords(): Promise<Envelope<NotAcceptedWords>> {
  return http.get<Envelope<NotAcceptedWords>>('/v1/bot-profile/not-accepted-words');
}

// ── Bots (21 September 2026) ───────────────────────────────────────────────
//
// An organisation has one or more bots. A bot is a name, a persona, how it
// behaves and the flows that are its own; a channel account (a WhatsApp
// number, a website chat, a Messenger Page, an Instagram account) is answered
// by exactly one bot, and a bot may answer on many. One bot is the default and
// answers wherever no other bot has been linked, so `getBotProfile` above (the
// default bot) is still the whole story for an organisation with one bot.
//
// Written by hand until the next `generate flows-llm`, which needs the stack
// running; the shapes mirror `BotDto` and friends in the service's contracts.

/** The channels a bot can answer on. `ussd` (4 October 2026): the account is one of the organisation's USSD codes (`ussd.listCodes()`). */
export type BotChannel = 'whatsapp' | 'web' | 'messenger' | 'instagram' | 'ussd';

/** One account a bot answers on: our id for the number, website chat, Page, Instagram account or USSD code. */
export interface BotChannelRef {
  channel: BotChannel;
  account_id: string;
}

export interface Bot {
  id: string;
  name: string;
  business_description?: string | null;
  tone: 'friendly' | 'formal' | 'playful' | string;
  language?: string | null;
  /** The website widget's opening line. */
  greeting?: string | null;
  behaviour: BotBehaviour;
  /** The default bot answers on every account that has not been given to another bot. */
  is_default: boolean;
  /** The accounts linked to this bot. The default bot also answers on every account linked to none. */
  channels: BotChannelRef[];
  flow_count: number;
  created_at: string;
  updated_at: string;
}

/** `POST /v1/bots`. Omitted words take the defaults, or the source bot's when `copy_from_bot_id` is given. */
export interface CreateBotRequest {
  name: string;
  business_description?: string | null;
  tone?: string | null;
  language?: string | null;
  greeting?: string | null;
  behaviour?: BotBehaviour | null;
  /** Start from this bot's persona and behaviour. Its flows and accounts are not copied. */
  copy_from_bot_id?: string;
}

/** `PUT /v1/bots/{id}`: the same fields as the bot profile. The name is required; a null behaviour leaves it alone. */
export type UpdateBotRequest = UpdateBotProfileRequest;

/** The plan's `bots` allowance as `GET /v1/bots` reports it. `allowance` is absent when unlimited: read `unlimited`. */
export interface BotAllowance {
  limit: string;
  allowance?: number | null;
  unlimited: boolean;
  current: number;
  plan_code?: string | null;
  plan_name?: string | null;
}

export interface BotListMeta extends Meta {
  count?: number;
  allowance?: BotAllowance;
}

/** Which bot answers on one channel account. An account with no link is answered by the default bot. */
export interface BotChannelLink extends BotChannelRef {
  bot_id: string;
  created_at: string;
}

/** The HTTP 409 `BOT_IN_USE` body of `deleteBot`: what still hangs off the bot. */
export interface BotInUse {
  code: 'BOT_IN_USE';
  message: string;
  flows: number;
  channels: number;
}

/** The organisation's bots, the default first, with the plan's allowance in `meta`. Never empty. */
export function listBots(): Promise<EnvelopeWithMeta<Bot[], BotListMeta>> {
  return http.get<EnvelopeWithMeta<Bot[], BotListMeta>>('/v1/bots');
}

/** Fails with 409 `PLAN_LIMIT_REACHED` (limit `bots`) once the plan's allowance is used; see `isPlanLimitReached`. */
export function createBot(req: CreateBotRequest): Promise<Envelope<Bot>> {
  return http.post<Envelope<Bot>>('/v1/bots', { body: req });
}

export function getBot(id: string): Promise<Envelope<Bot>> {
  return http.get<Envelope<Bot>>(`/v1/bots/${encodeURIComponent(id)}`);
}

export function updateBot(id: string, req: UpdateBotRequest): Promise<Envelope<Bot>> {
  return http.put<Envelope<Bot>>(`/v1/bots/${encodeURIComponent(id)}`, { body: req });
}

/**
 * Delete a bot. The default bot answers 409 `BOT_IS_DEFAULT`. A bot that still
 * has flows or accounts answers 409 `BOT_IN_USE` ({@link BotInUse}) unless
 * `reassignTo` names the bot that takes them over.
 */
export function deleteBot(id: string, opts: { reassignTo?: string } = {}): Promise<void> {
  return http.delete<void>(`/v1/bots/${encodeURIComponent(id)}`, opts.reassignTo ? { query: { reassign_to: opts.reassignTo } } : undefined);
}

/** Make this the bot that answers wherever no other bot has been linked. */
export function makeDefaultBot(id: string): Promise<Envelope<Bot>> {
  return http.post<Envelope<Bot>>(`/v1/bots/${encodeURIComponent(id)}/make-default`);
}

export function listBotChannelLinks(): Promise<Envelope<BotChannelLink[]>> {
  return http.get<Envelope<BotChannelLink[]>>('/v1/bots/channel-links');
}

/**
 * Give a number, website chat, Page or Instagram account to a bot, taking it
 * from whichever bot had it. 404 `ACCOUNT_NOT_FOUND` when the account is not
 * the organisation's; 503 `NUMBERS_UNAVAILABLE` when WhatsApp numbers could not
 * be checked just then.
 */
export function linkChannelToBot(req: BotChannelRef & { bot_id: string }): Promise<Envelope<BotChannelLink>> {
  return http.put<Envelope<BotChannelLink>>('/v1/bots/channel-links', { body: req });
}

/** Hand an account back to the default bot. Unlinking what was never linked is not an error. */
export function unlinkChannel(ref: BotChannelRef): Promise<void> {
  return http.delete<void>('/v1/bots/channel-links', { query: { channel: ref.channel, account_id: ref.account_id } });
}

// ── Corrections and conflicts in the knowledge base (3 October 2026) ─────

/** A fact the business typed itself; the bot puts it ahead of every document. */
export interface KbCorrection {
  id: string;
  /** What it is about, in a few words. */
  title: string;
  /** The correct information, as the bot should give it. */
  answer: string;
  is_active: boolean;
  /** False when it could not be embedded; it still matches questions by its words. */
  searchable: boolean;
  created_at: string;
  updated_at: string;
}

export type KbConflictStatus = 'open' | 'resolved' | 'dismissed';

/** Two documents that disagree on a fact. */
export interface KbConflict {
  id: string;
  topic: string;
  first_document_id: string;
  first_document_name: string | null;
  first_says: string;
  /** Null when the second side is a correction (a deep finding: a document that disagrees with what the business typed). */
  second_document_id: string | null;
  second_document_name: string | null;
  second_says: string;
  status: KbConflictStatus;
  /** The correction that settled it, when one did. */
  correction_id: string | null;
  found_at: string;
  closed_at: string | null;
  /** The correction a document disagrees with (deep check), instead of a second document. */
  second_correction_id?: string | null;
  second_correction_title?: string | null;
  /** `quick` (closest passages) or `deep` (every fact compared). */
  found_by?: KbConflictMode;
  /** A deep finding's exact words from each side. */
  first_quote?: string | null;
  second_quote?: string | null;
  /** Why the deep check's judge says they cannot both be true. */
  reason?: string | null;
}

/** `quick`: the documents whose passages are closest. `deep`: every fact in every document and correction (29 October 2026). */
export type KbConflictMode = 'quick' | 'deep';

/** One run of the checker: `document` (a new or changed document, automatic) or `all`. */
export interface KbConflictCheck {
  id: string;
  scope: 'document' | 'all';
  document_id: string | null;
  /** `skipped`: an automatic check the wallet could not pay for. */
  status: 'queued' | 'running' | 'done' | 'failed' | 'skipped';
  automatic: boolean;
  pairs_checked: number;
  conflicts_found: number;
  error: string | null;
  created_at: string;
  finished_at: string | null;
  mode?: KbConflictMode;
  /** A deep check's progress: documents and corrections to read and read, facts found, groups to judge and judged. */
  documents_total?: number;
  documents_read?: number;
  facts_found?: number;
  groups_total?: number;
  groups_judged?: number;
}

/** What checking the whole knowledge base would cost now (minor units, VAT included). */
export interface KbConflictEstimate {
  documents: number;
  pairs: number;
  estimated_minor: number;
  currency: string;
  available_minor: number | null;
  /** False when billing could not price it. */
  priced: boolean;
  affordable: boolean;
  mode?: KbConflictMode;
  /** Deep only: documents and corrections to read (new or changed), their characters, the facts already known. */
  to_read?: number;
  characters?: number;
  facts?: number;
  /** Groups the judge would read: exact when nothing is left to read (`groups_known`), otherwise an estimate. */
  groups?: number;
  groups_known?: boolean;
  /** The knowledge base is larger than one deep check reads; run it again to carry on. */
  capped?: boolean;
  reader_model?: string | null;
  judge_model?: string | null;
  /** Claude Sonnet is switched off, so the reader judges too. */
  judge_fallback?: boolean;
}

export function listKbCorrections(): Promise<Envelope<KbCorrection[]>> {
  return http.get<Envelope<KbCorrection[]>>('/v1/kb/corrections');
}

/** Add a correction. With `conflict_id` it settles that conflict. 409 `TOO_MANY_CORRECTIONS`. */
export function createKbCorrection(req: { title: string; answer: string; is_active?: boolean; conflict_id?: string }): Promise<Envelope<KbCorrection>> {
  return http.post<Envelope<KbCorrection>>('/v1/kb/corrections', { body: req });
}

/** Change a correction or switch it off (`is_active: false`). */
export function updateKbCorrection(id: string, req: { title?: string; answer?: string; is_active?: boolean }): Promise<Envelope<KbCorrection>> {
  return http.put<Envelope<KbCorrection>>(`/v1/kb/corrections/${encodeURIComponent(id)}`, { body: req });
}

export function deleteKbCorrection(id: string): Promise<void> {
  return http.delete(`/v1/kb/corrections/${encodeURIComponent(id)}`);
}

/** Conflicts, `open` by default. */
export function listKbConflicts(status: KbConflictStatus | 'all' = 'open'): Promise<Envelope<KbConflict[]>> {
  return http.get<Envelope<KbConflict[]>>('/v1/kb/conflicts', { query: { status } });
}

/** Not a conflict: it is not raised again. */
export function dismissKbConflict(id: string): Promise<void> {
  return http.post<void>(`/v1/kb/conflicts/${encodeURIComponent(id)}/dismiss`);
}

export function estimateKbConflictCheck(mode: KbConflictMode = 'quick'): Promise<Envelope<KbConflictEstimate>> {
  return http.get<Envelope<KbConflictEstimate>>('/v1/kb/conflicts/estimate', { query: { mode } });
}

/**
 * Check the whole knowledge base (charged as AI use): `quick`, or `deep` (every fact read by Claude Haiku and the
 * disagreements judged by Claude Sonnet; what was read before is not read again). 402 `INSUFFICIENT_CREDITS`,
 * 409 `CHECK_RUNNING` or `NOTHING_TO_COMPARE`.
 */
export function startKbConflictCheck(mode: KbConflictMode = 'quick'): Promise<Envelope<KbConflictCheck>> {
  return http.post<Envelope<KbConflictCheck>>('/v1/kb/conflicts/checks', { body: { mode } });
}

/** The latest checks, newest first. */
export function listKbConflictChecks(): Promise<Envelope<KbConflictCheck[]>> {
  return http.get<Envelope<KbConflictCheck[]>>('/v1/kb/conflicts/checks/latest');
}

/** Websites that embed the chat widget. The widget itself calls the public /v1/channels/web endpoints with the site key. */
export function listWebSites(): Promise<Envelope<WebSite[]>> {
  return http.get<Envelope<WebSite[]>>('/v1/web-sites');
}

export function createWebSite(req: CreateWebSiteRequest): Promise<Envelope<WebSite>> {
  return http.post<Envelope<WebSite>>('/v1/web-sites', { body: req });
}

/** Change a website chat's name, greeting and allowed websites. 400 VALIDATION, 404 NOT_FOUND. */
export function updateWebSite(id: string, req: UpdateWebSiteRequest): Promise<Envelope<WebSite>> {
  return http.put<Envelope<WebSite>>(`/v1/web-sites/${encodeURIComponent(id)}`, { body: req });
}

export function deleteWebSite(id: string): Promise<void> {
  return http.delete(`/v1/web-sites/${encodeURIComponent(id)}`);
}

/**
 * What the widget's state means (3 October 2026): a run state, or `idle` (nothing answers this),
 * `queued` (nothing answers it and it waits in the team's inbox), `with_team` (a person has the chat and
 * the bot stays quiet) or `closed` (the team blocked this visitor).
 */
export interface WebTurn {
  session_id: string;
  state: string;
  replies: FlowTestMessage[];
}

/**
 * One visitor message, as the widget sends it (public; the site key is the credential). Omit `session_id`
 * on the first message and keep what comes back. `reply_title` is a tapped button's words, for the inbox.
 */
export function sendWebMessage(siteKey: string, req: { session_id?: string | null; message?: string | null; reply_id?: string | null; reply_title?: string | null }): Promise<Envelope<WebTurn>> {
  return http.post<Envelope<WebTurn>>(`/v1/channels/web/${encodeURIComponent(siteKey)}/messages`, { body: req, auth: false });
}

/** A reply from the team for a website visitor (3 October 2026). */
export interface WebTeamReply { id: number; text: string; sender_name: string | null; created_at: string }

/** Team replies after `after` (the `cursor` of the last answer), oldest first. Public, like the widget. */
export function listWebReplies(siteKey: string, sessionId: string, after = 0): Promise<Envelope<{ replies: WebTeamReply[]; cursor: number }>> {
  return http.get<Envelope<{ replies: WebTeamReply[]; cursor: number }>>(`/v1/channels/web/${encodeURIComponent(siteKey)}/replies`, {
    query: { session_id: sessionId, after },
    auth: false
  });
}

export function startTest(flowId: string, req: StartTestRequest = {}): Promise<Envelope<FlowTestTurn>> {
  return http.post<Envelope<FlowTestTurn>>(`/v1/flows/${encodeURIComponent(flowId)}/test`, { body: req });
}

/**
 * The customer's next message. Send `reply_id` to tap a button or list row,
 * or `message` to type. Fails with 409 `SESSION_ENDED` once the flow ended.
 */
export function testTurn(flowId: string, sessionId: string, req: TestTurnRequest): Promise<Envelope<FlowTestTurn>> {
  return http.post<Envelope<FlowTestTurn>>(
    `/v1/flows/${encodeURIComponent(flowId)}/test/${encodeURIComponent(sessionId)}`,
    { body: req }
  );
}

/** Forget a test chat. */
export function endTest(flowId: string, sessionId: string): Promise<void> {
  return http.delete<void>(`/v1/flows/${encodeURIComponent(flowId)}/test/${encodeURIComponent(sessionId)}`);
}

/**
 * Start a run for a contact from your own system (an order was placed, a
 * booking is due), whatever the flow's trigger says. The flow must be active.
 * Any run already waiting on that contact is superseded. 202 Accepted.
 */
export function triggerFlow(flowId: string, req: TriggerFlowRequest): Promise<Envelope<TriggerFlowResult>> {
  return http.post<Envelope<TriggerFlowResult>>(`/v1/flows/${encodeURIComponent(flowId)}/trigger`, { body: req });
}

/**
 * The org's knowledge-base documents, newest first. W46: as with
 * {@link listFlowRuns}, `limit`/`cursor` are honoured and `meta` carries
 * `total` + `next_cursor` (the handler used to hard-code `Take(200)`).
 * Note the default page is now 50, not 200: pass `limit` for more.
 */
export function listKbDocuments(opts?: PageOpts): Promise<Page<KbDocument>> {
  return http.get<Page<KbDocument>>('/v1/kb/documents', { query: buildPageQuery(opts) });
}

/**
 * Ingest a text document. The service chunks it (~500 tokens, 100 overlap), embeds each
 * chunk, and returns `chunk_count` + `embedding_model` for what it wrote.
 */
/**
 * Ask the knowledge base as a customer would. Returns the answer the assistant
 * would give (its persona, the house rules, retrieval over your documents) and
 * the passages it read; `unsure` means it would take the not-sure path instead.
 */
/**
 * "Test this request" for an HTTP step: makes the call the step would make,
 * with sample values for its variables (`{ form: { email: 'a@b.c' } }`), and
 * returns the status, the body, every path the response offers (for the
 * extract rows and for the reply to the customer), the extract values and
 * the reply the customer would see.
 *
 * A saved step's sign-in secrets are never returned (its `auth.saved` names
 * them), so pass the flow and the step: their saved values fill the fields
 * left empty.
 */
export function probeHttp(
  node: Record<string, unknown>,
  sample?: Record<string, unknown>,
  saved?: { flowId: string; nodeId: string }
): Promise<Envelope<HttpProbeResult>> {
  return http.post<Envelope<HttpProbeResult>>('/v1/flows/http-probe', {
    body: { node, sample, ...(saved ? { flow_id: saved.flowId, node_id: saved.nodeId } : {}) }
  });
}

/**
 * What a customer's message would do: start a flow that says what it is for (`jump`), be answered as usual
 * with that flow as a button (`offer`), start a keyword flow (`keyword`) or nothing (`none`), with the score
 * of every candidate. A dry run: nothing is sent or charged. It reads the saved, active flows.
 */
export function testIntent(question: string, opts: { botId?: string; channel?: string } = {}): Promise<Envelope<IntentTestResult>> {
  return http.post<Envelope<IntentTestResult>>('/v1/flows/intent-test', { body: { question, bot_id: opts.botId, channel: opts.channel } });
}

/**
 * "Suggest more" under "Other ways customers say it" (18 October 2026): up to eight new ways a customer might ask for a
 * flow, from what it is for, its keywords and the ways already listed. Charged as AI tokens; 402
 * `INSUFFICIENT_CREDITS` when the credit is short. `botId` gives the language (the default chatbot when omitted).
 */
export function suggestIntentPhrases(input: { description?: string; keywords?: string[]; examples?: string[]; flowName?: string; botId?: string }): Promise<Envelope<IntentSuggestions>> {
  return http.post<Envelope<IntentSuggestions>>('/v1/flows/intent-suggestions', {
    body: { description: input.description, keywords: input.keywords, examples: input.examples, flow_name: input.flowName, bot_id: input.botId }
  });
}

/**
 * Which other active flows of the same chatbot these keywords clash with (18 October 2026), for the editor's warning.
 * `flowId` is the flow being edited: it is left out, and its chatbot is used.
 */
export function checkKeywords(keywords: string[], opts: { flowId?: string; botId?: string } = {}): Promise<Envelope<{ overlaps: KeywordOverlap[] }>> {
  return http.post<Envelope<{ overlaps: KeywordOverlap[] }>>('/v1/flows/keyword-check', { body: { keywords, flow_id: opts.flowId, bot_id: opts.botId } });
}

export function askKb(question: string, opts: { kbIds?: string[]; botId?: string } = {}): Promise<Envelope<KbAskResult>> {
  return http.post<Envelope<KbAskResult>>('/v1/kb/ask', { body: { question, kb_ids: opts.kbIds, bot_id: opts.botId } });
}

// ── Live sources in the knowledge base (1 October 2026) ─────────────────────

/** How a live source stays fresh: called when a customer asks, a copy refreshed on a schedule, or a copy refreshed by hand. */
export type LiveSourceMode = 'lookup' | 'refresh' | 'manual';
/** Minutes between refreshes a `refresh` source may use. */
export const LIVE_SOURCE_INTERVALS = [1, 5, 15, 60, 1440, 10080] as const;

/** The request a live source makes: the same shape as a Web API step. The address may use `{{detail}}`. */
export interface LiveSourceHttp {
  method?: 'GET' | 'POST';
  url: string;
  headers?: Record<string, string>;
  body?: unknown;
  content_type?: string;
  /**
   * Sign-in. The secret fields (`token`, `password`, `key_value`, `header_value`, `client_secret`) are
   * write-only: send them to set them, leave them out to keep what is stored. They are never returned.
   */
  auth?: Record<string, unknown>;
}

export interface LiveSourceParameter { name: string; description: string; required?: boolean }
/**
 * A part of the response to read. `key` is the data point's name in flows (`{{tour_slots.start}}`) and `description`
 * says what it is (19 October 2026). A path may use `[]` for every item of a list (`data.slots[].start`).
 */
export interface LiveSourcePath { path: string; label?: string; key?: string; description?: string }

export interface LiveSourceInput {
  name: string;
  /** What it answers, in plain words: how a customer's question finds it. */
  answers?: string;
  examples?: string[];
  mode: LiveSourceMode;
  request: LiveSourceHttp;
  /** Details taken from the customer's question (lookup only). */
  parameters?: LiveSourceParameter[];
  /** What to read from the response; empty reads all of it. */
  response_paths?: LiveSourcePath[];
  interval_minutes?: number | null;
  is_active?: boolean;
  /** An API connection: `request.url` is then a path on it, and the sign-in is the connection's. */
  connection_id?: string | null;
  /** The source's name in flows: a Look up step saves its data points under it (`tour_slots`). */
  key?: string | null;
}

export interface LiveSource extends Omit<LiveSourceInput, 'answers' | 'examples' | 'parameters' | 'response_paths' | 'is_active'> {
  id: string;
  answers: string;
  examples: string[];
  parameters: LiveSourceParameter[];
  response_paths: LiveSourcePath[];
  is_active: boolean;
  has_secret: boolean;
  /** Which secret fields are stored. Their values are never returned. */
  secret_fields: string[];
  status: 'new' | 'ok' | 'failing';
  last_error?: string | null;
  last_fetched_at?: string | null;
  last_changed_at?: string | null;
  next_fetch_at?: string | null;
  /** The knowledge document a stored copy lives in; null for a lookup source. */
  document_id?: string | null;
  created_at: string;
  updated_at: string;
}

export interface LiveSourceFetch { source: LiveSource; ok: boolean; changed: boolean; error?: string | null }

export interface LiveSourceTest {
  ok: boolean;
  error?: string | null;
  /** The text the assistant would read. */
  passage?: string | null;
  paths: HttpProbePath[];
  score?: number | null;
  match_at: number;
  matched?: boolean | null;
  values: Record<string, string>;
  missing: string[];
}

export function listKbSources(): Promise<Envelope<LiveSource[]>> {
  return http.get<Envelope<LiveSource[]>>('/v1/kb/sources');
}

export function getKbSource(id: string): Promise<Envelope<LiveSource>> {
  return http.get<Envelope<LiveSource>>(`/v1/kb/sources/${encodeURIComponent(id)}`);
}

/** Add a live source. A `refresh` or `manual` source is fetched at once and stored as a knowledge document. */
export function createKbSource(req: LiveSourceInput): Promise<Envelope<LiveSource>> {
  return http.post<Envelope<LiveSource>>('/v1/kb/sources', { body: req });
}

/** Send the whole source. A secret field left out keeps the stored value. */
export function updateKbSource(id: string, req: LiveSourceInput): Promise<Envelope<LiveSource>> {
  return http.put<Envelope<LiveSource>>(`/v1/kb/sources/${encodeURIComponent(id)}`, { body: req });
}

/** Removes the source and its stored copy. */
export function deleteKbSource(id: string): Promise<void> {
  return http.delete<void>(`/v1/kb/sources/${encodeURIComponent(id)}`);
}

/** Refresh a stored copy now. `changed` false means nothing was written and nothing was charged. */
export function fetchKbSourceNow(id: string): Promise<Envelope<LiveSourceFetch>> {
  return http.post<Envelope<LiveSourceFetch>>(`/v1/kb/sources/${encodeURIComponent(id)}/fetch-now`, { body: {} });
}

/**
 * Try a source without saving it: the address is called once and the text the assistant would read comes
 * back with every part of the response. With `question`, also whether the question finds the source and
 * the details taken from it. Pass `id` to test a saved source or to use its stored secrets for a draft.
 */
export function testKbSource(req: { id?: string; source?: LiveSourceInput; question?: string; values?: Record<string, string> }): Promise<Envelope<LiveSourceTest>> {
  return http.post<Envelope<LiveSourceTest>>('/v1/kb/sources/test', { body: req });
}

// ── API connections (19 October 2026) ───────────────────────────────────────
//
// A business's own API, added from its documentation: the base address and the sign-in (secrets sealed once), with
// live sources that answer questions and actions a flow's Web API step can send. The wizard reads the documentation,
// tries the sign-in, has the AI draft the chosen endpoints, and sets them up in one go.

/** The AI reads documentation and drafts endpoints for up to this long. */
export const API_DOCS_TIMEOUT_MS = 110_000;

export type ApiAuthType = 'none' | 'bearer' | 'basic' | 'api_key' | 'custom_header' | 'oauth2';
export type ApiDocKind = 'openapi' | 'postman' | 'markdown' | 'pdf' | 'manual';

export interface ApiDocParam { name: string; in: 'path' | 'query' | 'body' | 'header'; description?: string | null; required: boolean; example?: string | null }
export interface ApiDocField { path: string; description?: string | null }
export interface ApiDocEndpoint {
  method: string;
  /** Uses `{{name}}` for each detail in the path. */
  path: string;
  summary?: string | null;
  description?: string | null;
  parameters: ApiDocParam[];
  body_example?: unknown;
  fields: ApiDocField[];
  response_example?: unknown;
  /** `read` answers questions (a live source); `action` does something (a flow's Web API step). */
  kind: 'read' | 'action';
  group?: string | null;
}
export interface ApiDocAuth { type: ApiAuthType; key_name?: string | null; in?: 'header' | 'query' | null; token_url?: string | null; scope?: string | null; note?: string | null }
export interface ApiDoc { kind: ApiDocKind; title?: string | null; base_url?: string | null; auth: ApiDocAuth; endpoints: ApiDocEndpoint[] }

export interface ApiDocsRead {
  doc: ApiDoc;
  /** True when the AI read it (charged as AI tokens); OpenAPI and Postman are read free. */
  read_by_ai: boolean;
  /** Only the start of very long documentation was read. */
  shortened: boolean;
  /** More live sources the plan's knowledge allowance takes; null for no limit. */
  places_left?: number | null;
}

export interface ApiConnectionInput {
  name: string;
  base_url: string;
  /**
   * Sign-in. The secret fields (`token`, `password`, `key_value`, `header_value`, `client_secret`) are write-only:
   * send them to set them, leave them out to keep what is stored.
   */
  auth?: { type: ApiAuthType } & Record<string, unknown>;
  /** Sent with every call. Never a key: keys go in `auth`. */
  headers?: Record<string, string>;
  docs_kind?: ApiDocKind;
  docs_title?: string | null;
}

export interface ApiActionInput {
  name: string;
  description?: string | null;
  method: string;
  /** A path on the connection, with `{{name}}` for each detail. */
  path: string;
  parameters?: ApiDocParam[];
  body?: unknown;
  fields?: { path: string; key?: string; label?: string; description?: string | null }[];
}

export interface ApiAction extends Required<Pick<ApiActionInput, 'name' | 'method' | 'path'>> {
  id: string;
  connection_id: string;
  description?: string | null;
  parameters: ApiDocParam[];
  body?: unknown;
  fields: { path: string; key?: string; label?: string; description?: string | null }[];
  created_at: string;
}

export interface ApiConnection {
  id: string;
  name: string;
  base_url: string;
  auth: { type: ApiAuthType } & Record<string, unknown>;
  /** Which secret fields are stored. Their values are never returned. */
  secret_fields: string[];
  headers: Record<string, string>;
  docs_kind: ApiDocKind;
  docs_title?: string | null;
  status: 'new' | 'ok' | 'failing';
  last_error?: string | null;
  last_tested_at?: string | null;
  sources: { id: string; name: string; key?: string | null; mode: LiveSourceMode; status: string; is_active: boolean }[];
  actions: ApiAction[];
  created_at: string;
  updated_at: string;
}

export interface ApiCallTest {
  ok: boolean;
  status: number;
  error?: string | null;
  /** The answer as readable text, shortened. */
  preview?: string | null;
  paths: HttpProbePath[];
  /** Details that need a value before the call can be made. */
  missing: string[];
}

export interface ApiDraft {
  index: number;
  /** The live source to add, ready to change. */
  source: LiveSourceInput;
  /** Every field of the endpoint, for choosing more data points. */
  all_fields: string[];
  tested: boolean;
  test_ok: boolean;
  test_status: number;
  test_error?: string | null;
  sample?: string | null;
}

export interface ApiDrafts {
  drafts: ApiDraft[];
  /** Set when the AI could not draft (no credit): the drafts were made by rules. */
  ai_note?: string | null;
}

export interface ApiSetup { connection: ApiConnection; sources: LiveSource[] }

export function listApiConnections(): Promise<Envelope<ApiConnection[]>> {
  return http.get<Envelope<ApiConnection[]>>('/v1/api-connections');
}

export function getApiConnection(id: string): Promise<Envelope<ApiConnection>> {
  return http.get<Envelope<ApiConnection>>(`/v1/api-connections/${encodeURIComponent(id)}`);
}

/**
 * Read an API's documentation into its endpoints. Send `text` (OpenAPI or Swagger in JSON or YAML, a Postman
 * collection, or Markdown) or the `media_id` of an uploaded PDF. Nothing is stored.
 */
export function readApiDocs(req: { text?: string; media_id?: string; file_name?: string }): Promise<Envelope<ApiDocsRead>> {
  return http.post<Envelope<ApiDocsRead>>('/v1/api-connections/read-docs', { body: req, timeoutMs: API_DOCS_TIMEOUT_MS });
}

/** Try the sign-in on one endpoint that reads. An endpoint that does something is never called here. */
export function testApiConnection(req: { connection_id?: string; connection?: ApiConnectionInput; endpoint: ApiDocEndpoint; values?: Record<string, string> }): Promise<Envelope<ApiCallTest>> {
  return http.post<Envelope<ApiCallTest>>('/v1/api-connections/test', { body: req });
}

/** The AI's draft of each chosen endpoint (at most 12) as a live source with data points. Charged as AI tokens. */
export function draftApiSources(req: { connection_id?: string; connection?: ApiConnectionInput; api_name?: string; endpoints: ApiDocEndpoint[] }): Promise<Envelope<ApiDrafts>> {
  return http.post<Envelope<ApiDrafts>>('/v1/api-connections/drafts', { body: req, timeoutMs: API_DOCS_TIMEOUT_MS });
}

/** Add a connection (or add to a saved one) with its live sources and actions, all or nothing. */
export function setUpApiConnection(req: { connection_id?: string; connection?: ApiConnectionInput; sources?: LiveSourceInput[]; actions?: ApiActionInput[] }): Promise<Envelope<ApiSetup>> {
  return http.post<Envelope<ApiSetup>>('/v1/api-connections/setup', { body: req });
}

/** Change the address or sign-in. A secret left out keeps the stored one; a new kind of sign-in drops them. */
export function updateApiConnection(id: string, req: ApiConnectionInput): Promise<Envelope<ApiConnection>> {
  return http.put<Envelope<ApiConnection>>(`/v1/api-connections/${encodeURIComponent(id)}`, { body: req });
}

/** Removes the connection with its live sources and actions. Flow steps that used it take their error path. */
export function deleteApiConnection(id: string): Promise<void> {
  return http.delete<void>(`/v1/api-connections/${encodeURIComponent(id)}`);
}

export function deleteApiAction(connectionId: string, actionId: string): Promise<void> {
  return http.delete<void>(`/v1/api-connections/${encodeURIComponent(connectionId)}/actions/${encodeURIComponent(actionId)}`);
}

// ── The API call log (19 October 2026) ───────────────────────────────────────
//
// Every call a flow run made to another system, for 48 hours: what was sent and what came back, with every key,
// token and password the call signed in with replaced by "•••• (hidden)". Owners and admins only.

/** How long a step keeps an API's answer: `cache: { minutes, share }` on an HTTP request or Look up step. */
export const CACHE_MINUTES = [1, 5, 15, 60, 360, 1440] as const;
export type CacheShare = 'everyone' | 'conversation';

export type HttpCallPurpose = 'step' | 'lookup' | 'live_source' | 'notify';

export interface HttpCall {
  id: string;
  flow_id?: string | null;
  flow_name?: string | null;
  run_id?: string | null;
  node_id?: string | null;
  node_name?: string | null;
  channel?: string | null;
  purpose: HttpCallPurpose;
  method: string;
  /** The address called; a key in its query string is hidden. */
  url: string;
  /** 0 when no answer came (a timeout, a refused address). */
  status: number;
  ok: boolean;
  duration_ms: number;
  /** Answered from the step's cache: the other system was not called. */
  from_cache: boolean;
  error?: string | null;
  created_at: string;
  /** When it is deleted. */
  expires_at: string;
}

/** Each header as [name, value]; a credential's value reads "•••• (hidden)". */
export interface HttpCallSide { headers: [string, string][]; body?: string | null }

export interface HttpCallDetail { call: HttpCall; request: HttpCallSide; response: HttpCallSide }

/** The API calls flows made in the last 48 hours, newest first. Page with `before` (the last entry's created_at). */
export function listHttpLogs(params?: { flow_id?: string; run_id?: string; failed?: boolean; before?: string; limit?: number }): Promise<Envelope<HttpCall[]>> {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params ?? {})) if (v !== undefined && v !== null && v !== '') q.set(k, String(v));
  const qs = q.toString();
  return http.get<Envelope<HttpCall[]>>(`/v1/flows/http-logs${qs ? `?${qs}` : ''}`);
}

/** One API call with its headers and bodies. 404 once it is older than 48 hours. */
export function getHttpLog(id: string): Promise<Envelope<HttpCallDetail>> {
  return http.get<Envelope<HttpCallDetail>>(`/v1/flows/http-logs/${encodeURIComponent(id)}`);
}

// ── Websites in the knowledge base (3 October 2026) ────────────────────────
//
// A website is analysed first (its pages found, and the ones worth reading
// suggested within the plan's room), then the chosen pages are read into one
// knowledge document each, and the site is read again by hand when it changes.

export type KbWebsiteStatus = 'new' | 'analysing' | 'analysed' | 'reading' | 'ready' | 'failing';

/** Why a page found is not suggested. `no_room`: worth reading, but past the plan's room. */
export type KbWebsitePageReason = 'few_words' | 'duplicate' | 'private' | 'not_page' | 'noindex' | 'robots' | 'failed' | 'skipped' | 'no_room';

export interface KbWebsitePage {
  url: string;
  title: string;
  words: number;
  depth: number;
  suggested: boolean;
  /** Chosen to be read. */
  chosen: boolean;
  /** Found on reading again and not yet chosen. */
  new: boolean;
  reason?: KbWebsitePageReason | null;
}

export interface KbWebsiteAnalysis {
  found?: number;
  /** Pages worth reading, whether or not there is room. */
  worth?: number;
  suggested?: number;
  /** Knowledge documents the plan has room for, this site's own pages included; null: no limit. */
  room?: number | null;
  allowance?: number | null;
  /** False when the site had more pages than one analysis looks at (300). */
  complete?: boolean;
  new_pages?: number;
}

export interface KbWebsiteCounts {
  added?: number;
  updated?: number;
  unchanged?: number;
  removed?: number;
  failed?: number;
  no_room?: number;
}

export interface KbWebsite {
  id: string;
  start_url: string;
  host: string;
  name: string;
  status: KbWebsiteStatus;
  /** The job waiting or running: `analyse`, `read` or `reread`. */
  job?: 'analyse' | 'read' | 'reread' | null;
  last_error?: string | null;
  analysis: KbWebsiteAnalysis;
  selected_count: number;
  documents: number;
  last_counts: KbWebsiteCounts;
  skip_prefixes: string[];
  analysed_at?: string | null;
  read_finished_at?: string | null;
  created_at: string;
  /** Only from {@link getKbWebsite}. */
  pages?: KbWebsitePage[] | null;
  selected?: string[] | null;
}

export function listKbWebsites(): Promise<Envelope<KbWebsite[]>> {
  return http.get<Envelope<KbWebsite[]>>('/v1/kb/websites');
}

/** One website with every page found. */
export function getKbWebsite(id: string): Promise<Envelope<KbWebsite>> {
  return http.get<Envelope<KbWebsite>>(`/v1/kb/websites/${encodeURIComponent(id)}`);
}

/** Add a website. It is analysed in the background (202); nothing is charged for analysing. */
export function createKbWebsite(req: { url: string; skip_prefixes?: string[] }): Promise<Envelope<KbWebsite>> {
  return http.post<Envelope<KbWebsite>>('/v1/kb/websites', { body: req });
}

/** Look at the website again and suggest pages, without reading any. */
export function analyseKbWebsite(id: string): Promise<Envelope<KbWebsite>> {
  return http.post<Envelope<KbWebsite>>(`/v1/kb/websites/${encodeURIComponent(id)}/analyse`, { body: {} });
}

/** Choose the pages to read. Refused with 409 `PLAN_LIMIT_REACHED` past the plan's room. */
export function selectKbWebsitePages(id: string, req: { urls: string[]; skip_prefixes?: string[] }): Promise<Envelope<KbWebsite>> {
  return http.put<Envelope<KbWebsite>>(`/v1/kb/websites/${encodeURIComponent(id)}/selection`, { body: req });
}

/**
 * Read the chosen pages into the knowledge base, or read the website again. Unchanged pages cost
 * nothing; changed pages pay only for reading their text again; pages that are gone are removed.
 */
export function readKbWebsite(id: string): Promise<Envelope<KbWebsite>> {
  return http.post<Envelope<KbWebsite>>(`/v1/kb/websites/${encodeURIComponent(id)}/read`, { body: {} });
}

/** Remove the website and its pages from the knowledge base. */
export function deleteKbWebsite(id: string): Promise<void> {
  return http.delete<void>(`/v1/kb/websites/${encodeURIComponent(id)}`);
}

export function createKbDocument(req: CreateKbDocumentRequest): Promise<Envelope<KbDocument>> {
  return http.post<Envelope<KbDocument>>('/v1/kb/documents', { body: req });
}

/**
 * Ingest a previously uploaded media object's extracted text (W18 path). The
 * body is the generated `IngestFromMediaRequest`; the two-argument signature is
 * kept as-is so call sites do not change.
 */
export function ingestKbFromMedia(mediaId: string, name?: string): Promise<Envelope<KbDocument>> {
  return http.post<Envelope<KbDocument>>('/v1/kb/documents/from-media', {
    body: { media_id: mediaId, name }
  });
}

/** Convenience over {@link createKbDocument}: reads the file's text client-side. */
export async function uploadKbDocument(file: File, meta?: { name?: string }): Promise<Envelope<KbDocument>> {
  return createKbDocument({ name: meta?.name ?? file.name, body: await file.text() });
}

/** One document with its text and, when ingested from the library, its source file. */
export function getKbDocument(id: string): Promise<Envelope<KbDocumentFull>> {
  return http.get<Envelope<KbDocumentFull>>(`/v1/kb/documents/${encodeURIComponent(id)}`);
}

export function deleteKbDocument(id: string): Promise<void> {
  return http.delete<void>(`/v1/kb/documents/${encodeURIComponent(id)}`);
}

/**
 * Add up to 50 already-uploaded files at once (`POST /v1/kb/documents/batch`).
 *
 * Answers as soon as the files are queued: each becomes a `queued` document and
 * a background worker indexes them one at a time, so the caller does not wait on
 * the embedding model and a closed tab does not stop anything. Poll
 * {@link listKbDocuments} until {@link isKbDocumentPending} is false for each.
 * A file over the plan's allowance, already added, or with no readable text is
 * returned in `refused` with a stable `code` and is not queued.
 */
export function addKbDocuments(items: KbBatchItem[]): Promise<Envelope<KbBatchResult>> {
  return http.post<Envelope<KbBatchResult>>('/v1/kb/documents/batch', { body: { items } });
}

/** Queue a `failed` document again (`POST /v1/kb/documents/{id}/retry`). 409 for any other status. */
export function retryKbDocument(id: string): Promise<Envelope<KbDocument>> {
  return http.post<Envelope<KbDocument>>(`/v1/kb/documents/${encodeURIComponent(id)}/retry`, {});
}

/**
 * Run the retrieval a flow's LLM node would run. `kb_ids` empty searches every document
 * in the organisation. The body is the generated `KbSearchRequest`; the
 * `(query, opts)` signature is hand-written so call sites do not change.
 */
export function searchKb(
  query: string,
  opts?: { kb_ids?: string[]; k?: number }
): Promise<Envelope<KbSearchResult>> {
  return http.post<Envelope<KbSearchResult>>('/v1/kb/search', {
    body: { query, kb_ids: opts?.kb_ids, k: opts?.k }
  });
}

/**
 * Re-chunk and re-embed every document in the organisation. Required after switching
 * embedding model: vectors from a different model are not comparable.
 */
export function reindexKb(): Promise<Envelope<KbReindexResult>> {
  return http.post<Envelope<KbReindexResult>>('/v1/kb/reindex', {});
}

/** Platform-admin: which chat + embedding provider flows-llm is running. */
export function getLlmProvider(): Promise<Envelope<LlmProvider>> {
  return http.get<Envelope<LlmProvider>>('/v1/llm/provider');
}

// ── Facebook Messenger and Instagram Direct ────────────────────────────────
//
// The Meta messaging adapter lives in flows-llm, beside the engine and the runs
// it feeds, so these sit here rather than in `channel-wa.ts`. WhatsApp keeps
// its own module because it is its own product with its own account model,
// its own templates and its own review.
//
// Hand-written rather than generated: the generator has not been run since these
// endpoints landed, and re-running it is the owner's step, not this change's.

/** Whether a Facebook page can be connected at all, and what to ask Facebook for. */
export interface MetaConnectInfo {
  /** False when the platform's own Meta app is not set up. Nothing the customer can fix. */
  available: boolean;
  app_id?: string | null;
  /** The Graph version the browser's login dialog must use, so no client pins its own. */
  graph_version?: string | null;
  scopes: string[];
  /** One sentence, when `available` is false. Never the underlying fault. */
  reason?: string | null;
  /**
   * The Facebook Login for Business configuration to open the login dialog with, in place of `scopes`
   * (8 October 2026). Null when the platform has not set one: ask for `scopes` by name instead.
   */
  config_id?: string | null;
  /**
   * True when this person may connect a Page through ManyChat instead (23 October 2026): platform staff acting for the
   * business, until Meta approves the platform's messaging permissions. See {@link connectManyChat}.
   */
  relay_available?: boolean;
}

/** A Facebook page the person signing in administers. */
export interface MetaConnectablePage {
  id: string;
  name: string;
  category?: string | null;
  /** They hold the MESSAGING or MODERATE task. Without one, Meta refuses every send. */
  can_message: boolean;
  /** They may subscribe an app to the page. Without this, no message would ever arrive. */
  can_subscribe: boolean;
  instagram_id?: string | null;
  instagram_username?: string | null;
  /** An Instagram account is linked but is a personal one: connected-looking, and unmessageable. */
  instagram_needs_upgrade: boolean;
}

export interface MetaConnectablePages {
  /** The long-lived token, handed straight back on {@link connectMetaAccount}. */
  user_access_token: string;
  pages: MetaConnectablePage[];
}

/** A connected Facebook page or Instagram account. */
export interface MetaAccount {
  id: string;
  /** `messenger` | `instagram`. */
  channel: string;
  /** What inbound messages are addressed to: the page id, or the Instagram account id. */
  external_id: string;
  page_id: string;
  page_name?: string | null;
  ig_username?: string | null;
  /** What to call this on screen: the page name, or the Instagram handle. */
  label: string;
  is_active: boolean;
  /** When message delivery was switched on. Null means nothing will arrive. */
  subscribed_at?: string | null;
  last_check_at?: string | null;
  /** Three-state: `null` is "never checked", which is not "broken". */
  last_check_ok?: boolean | null;
  last_check_detail?: string | null;
  created_at: string;
  /**
   * When Facebook stopped accepting this Page's connection (16 October 2026); null while it works. Connecting the
   * Page again clears it, and so does a reply that goes through or a check that passes.
   */
  broken_since?: string | null;
  /** `TOKEN_EXPIRED`, `NO_PAGE_ACCESS`, `NOT_SUBSCRIBED`, or `CONNECTION_CHECK` when the daily check found it. */
  broken_code?: string | null;
  broken_detail?: string | null;
  /** Null when connected directly; `manychat` when the Page is reached through ManyChat (23 October 2026). */
  via?: string | null;
}

/** Connect a Page through ManyChat: which channel, and the API key from ManyChat → Settings → API. */
export interface ConnectManyChatRequest {
  /** `messenger` | `instagram`. */
  channel: string;
  api_key: string;
}

/**
 * What to paste into ManyChat's External Request (its Default Reply). Returned only when connecting or making a new
 * key, never by {@link listMetaAccounts}: the key lets anyone post messages as this Page's customers.
 */
export interface ManyChatSetup {
  account: MetaAccount;
  /** The address ManyChat posts each message to. */
  url: string;
  header_name: string;
  header_value: string;
  /** The JSON body, with ManyChat's own contact fields. */
  body: string;
}

export interface MetaReadinessCheck {
  /** A name prefixed `warn.` is a question Meta would not answer: amber, not red. */
  name: string;
  ok: boolean;
  detail: string;
}

export interface MetaReadiness {
  ok: boolean;
  /** One line per check, failures first. Render with `white-space: pre-line`. */
  summary: string;
  checks: MetaReadinessCheck[];
}

export interface ConnectMetaAccountRequest {
  /** `messenger` | `instagram`. */
  channel: string;
  page_id: string;
  /**
   * The long-lived token from {@link listConnectableMetaPages}.
   *
   * The page's own access token is minted from this on the server and is never
   * handed to a browser: it authorises every message the account will ever
   * send and does not expire, so it has no business in a tab, a network log or
   * a bug report.
   */
  user_access_token: string;
}

/**
 * Ask before loading Facebook's SDK, always.
 *
 * Loading it against an app that cannot do this produces "This app needs at
 * least one supported permission", which tells a customer nothing and sends
 * them looking in their own settings for a fault that is the platform's.
 */
export function getMetaConnectInfo(): Promise<Envelope<MetaConnectInfo>> {
  return http.get<Envelope<MetaConnectInfo>>('/v1/channels/meta/connect');
}

/**
 * Exchange the browser's short-lived token for a long-lived one and list what
 * this person administers. Nothing is stored: they have not chosen a page yet.
 */
export function listConnectableMetaPages(userAccessToken: string): Promise<Envelope<MetaConnectablePages>> {
  return http.post<Envelope<MetaConnectablePages>>('/v1/channels/meta/connect/pages', {
    body: { user_access_token: userAccessToken }
  });
}

/** Connected Facebook pages and Instagram accounts. */
export function listMetaAccounts(): Promise<Envelope<MetaAccount[]>> {
  return http.get<Envelope<MetaAccount[]>>('/v1/channels/meta/accounts');
}

/**
 * Connect one page, or the Instagram account behind it.
 *
 * Proves the token can read the page, proves Instagram is professional and
 * linked, subscribes the app to the page's webhooks and stores the credential,
 * in that order, so a refusal leaves nothing behind.
 */
export function connectMetaAccount(req: ConnectMetaAccountRequest): Promise<Envelope<MetaAccount>> {
  return http.post<Envelope<MetaAccount>>('/v1/channels/meta/accounts', { body: req });
}

/** Prove a connected page can receive and send. Asks Meta; never infers. */
export function checkMetaAccount(id: string): Promise<Envelope<MetaReadiness>> {
  return http.post<Envelope<MetaReadiness>>(`/v1/channels/meta/accounts/${encodeURIComponent(id)}/check`, {});
}

/** Disconnect. The stored token stays, so reconnecting is a click rather than another trip through Facebook. */
export function disconnectMetaAccount(id: string): Promise<void> {
  return http.delete<void>(`/v1/channels/meta/accounts/${encodeURIComponent(id)}`);
}

/**
 * Connect a Facebook Page or Instagram account through ManyChat, until Meta approves the platform's own messaging
 * permissions (23 October 2026). Checks the key with ManyChat and keeps it sealed. Only platform staff acting for the
 * business may; everyone else gets 403. Connecting again with a new API key keeps the setup already pasted in ManyChat.
 */
export function connectManyChat(req: ConnectManyChatRequest): Promise<Envelope<ManyChatSetup>> {
  return http.post<Envelope<ManyChatSetup>>('/v1/channels/meta/manychat', { body: req });
}

/** A new key for ManyChat to send messages with. What is pasted in ManyChat must be changed to match. */
export function newManyChatKey(accountId: string): Promise<Envelope<ManyChatSetup>> {
  return http.post<Envelope<ManyChatSetup>>(`/v1/channels/meta/manychat/${encodeURIComponent(accountId)}/key`, {});
}

// ── The bot coach (8 October 2026) ──────────────────────────────────────────
//
// Reviews the chatbot's recent conversations, on request or on a daily or weekly schedule in the business's own
// time zone, and suggests fixes that an owner or admin applies or dismisses. Charged as AI use at the plan's
// token rate; a review is priced first.

export type BotCoachSchedule = 'off' | 'daily' | 'weekly';
/** `keywords` and `document` come from the deep review (29 October 2026). */
export type BotCoachSuggestionKind = 'correction' | 'flow_wording' | 'persona' | 'note' | 'keywords' | 'document';
/** `quick`: the coach's review. `deep`: every chatbot reply judged against the knowledge base and the routing. */
export type BotCoachMode = 'quick' | 'deep';
/** What the deep review says of a chatbot reply. */
export type ReviewVerdict = 'ok' | 'wrong' | 'assumed' | 'missed' | 'not_known' | 'misrouted' | 'stuck';

export interface BotCoachSettings {
  schedule: BotCoachSchedule;
  /** 0 = Sunday .. 6 = Saturday, for a weekly review. */
  weekday: number;
  /** HH:mm in the business's own time zone. */
  local_time: string;
  /** The most conversations one review reads (5 to 100), which bounds its cost. */
  max_conversations: number;
  next_run_at?: string | null;
  /** The deep review's own schedule, on the same weekday and time. */
  deep_schedule?: BotCoachSchedule;
  /** The most conversations one deep review reads (1 to 100). */
  deep_max_conversations?: number;
  deep_next_run_at?: string | null;
}

export interface BotCoachRun {
  id: string;
  status: 'queued' | 'running' | 'done' | 'failed' | 'skipped';
  automatic: boolean;
  since: string;
  conversations: number;
  suggestions_found: number;
  summary?: string | null;
  error?: string | null;
  created_at: string;
  finished_at?: string | null;
  mode?: BotCoachMode;
  /** A deep review's chatbot replies by verdict. */
  counts?: Partial<Record<ReviewVerdict, number>> | null;
}

/** Where a deep finding's right answer is written: a document or a correction. */
export interface ReviewSource {
  ref: string;
  kind: 'document' | 'correction';
  id: string;
  name: string;
}

export interface BotCoachSuggestion {
  id: string;
  run_id?: string | null;
  kind: BotCoachSuggestionKind;
  title: string;
  problem: string;
  /** The conversation it came from: `conversation_id` and a few `lines`. */
  evidence: { conversation_id?: string | null; lines?: string[] };
  /**
   * The fix, by kind: {title, answer} | {flow_id, node_id, field, before, after} | {field, before, after} | {} |
   * {flow_id, flow_name, before: string[], after: string[]} (keywords) | {document_id, document_name} (document).
   */
  proposal: Record<string, string | string[]>;
  status: 'open' | 'applied' | 'dismissed';
  created_at: string;
  resolved_at?: string | null;
  /** A deep review's finding: its verdict, the exchange, what is right and where that is written. */
  verdict?: ReviewVerdict | null;
  customer_said?: string | null;
  bot_said?: string | null;
  right_answer?: string | null;
  sources?: ReviewSource[] | null;
}

export interface BotCoachOverview {
  settings: BotCoachSettings;
  latest_run?: BotCoachRun | null;
  open_suggestions: number;
}

export interface BotCoachEstimate {
  conversations: number;
  estimated_minor: number;
  currency: string;
  available_minor?: number | null;
  priced: boolean;
  affordable: boolean;
  since: string;
  mode?: BotCoachMode;
  /** Deep only: the model that judges, and whether it stands in for Claude Sonnet (switched off). */
  judge_model?: string | null;
  judge_fallback?: boolean;
}

export function getBotCoach(): Promise<Envelope<BotCoachOverview>> {
  return http.get<Envelope<BotCoachOverview>>('/v1/bot/coach');
}

/** Any field left out keeps what is saved, so the deep schedule can be saved on its own. */
export function saveBotCoachSettings(req: Partial<Omit<BotCoachSettings, 'next_run_at' | 'deep_next_run_at'>>): Promise<Envelope<BotCoachSettings>> {
  return http.put<Envelope<BotCoachSettings>>('/v1/bot/coach/settings', { body: req });
}

/** What a review now would read and cost (`quick` or `deep`). Ask before starting one. */
export function estimateBotCoach(mode: BotCoachMode = 'quick'): Promise<Envelope<BotCoachEstimate>> {
  return http.get<Envelope<BotCoachEstimate>>('/v1/bot/coach/estimate', { query: { mode } });
}

/**
 * Review now: the coach's review, or the deep review (every chatbot reply judged by Claude Sonnet against the knowledge
 * base and the routing). 409 RUN_ACTIVE or NOTHING_TO_REVIEW; 402 INSUFFICIENT_CREDITS.
 */
export function startBotCoachRun(mode: BotCoachMode = 'quick'): Promise<Envelope<BotCoachRun>> {
  return http.post<Envelope<BotCoachRun>>('/v1/bot/coach/runs', { body: { mode } });
}

export function listBotCoachRuns(limit?: number): Promise<Envelope<BotCoachRun[]>> {
  return http.get<Envelope<BotCoachRun[]>>(`/v1/bot/coach/runs${limit ? `?limit=${limit}` : ''}`);
}

export function listBotCoachSuggestions(opts: { status?: 'open' | 'applied' | 'dismissed'; limit?: number } = {}): Promise<Envelope<BotCoachSuggestion[]>> {
  const q = new URLSearchParams();
  if (opts.status) q.set('status', opts.status);
  if (opts.limit !== undefined) q.set('limit', String(opts.limit));
  const qs = q.toString();
  return http.get<Envelope<BotCoachSuggestion[]>>(`/v1/bot/coach/suggestions${qs ? `?${qs}` : ''}`);
}

/** Apply a suggestion; `proposal` carries any words the person changed first. */
export function applyBotCoachSuggestion(id: string, proposal?: Record<string, string | string[]>): Promise<Envelope<BotCoachSuggestion>> {
  return http.post<Envelope<BotCoachSuggestion>>(`/v1/bot/coach/suggestions/${encodeURIComponent(id)}/apply`, { body: { proposal } });
}

export function dismissBotCoachSuggestion(id: string): Promise<Envelope<BotCoachSuggestion>> {
  return http.post<Envelope<BotCoachSuggestion>>(`/v1/bot/coach/suggestions/${encodeURIComponent(id)}/dismiss`);
}

// ── The console assistant (8 October 2026) ──────────────────────────────────
//
// The console's own assistant, for everyone signed in (agents too). Stateless: send the message, the last few
// turns and where the person is. The answer carries words and at most two actions, which the console confirms with
// the person before carrying out. Charged as AI use at the plan's token rate; 402 INSUFFICIENT_CREDITS when the
// wallet cannot pay for a message.

export type ConsoleAssistantActionType = 'navigate' | 'set_theme' | 'set_language' | 'set_density' | 'start_tour' | 'open_guide';

export interface ConsoleAssistantAction {
  type: ConsoleAssistantActionType;
  /** navigate: a page from the context's `pages`, optionally with `?tab=name`. */
  path?: string | null;
  /** set_theme: light | dark | system; set_language: a code from `languages`; set_density: compact | comfortable | spacious. */
  value?: string | null;
  /** start_tour and open_guide: an id from `guides`. */
  guide?: string | null;
  /** What will happen, in the person's language. */
  label: string;
}

export interface ConsoleAssistantContext {
  path?: string;
  page_title?: string;
  user_name?: string;
  business_name?: string;
  language?: string;
  theme?: string;
  density?: string;
  credits?: string;
  /** Every page the person may open: the only places `navigate` may go. */
  pages?: { path: string; title: string; about?: string }[];
  guides?: { id: string; title: string }[];
  languages?: string[];
  /** The written guide of the module the person is in, as plain text. */
  page_guide?: string;
}

export interface ConsoleAssistantRequest {
  message: string;
  /** The last turns, oldest first (12 at most are read). */
  history?: { role: 'person' | 'assistant'; text: string }[];
  context?: ConsoleAssistantContext;
  /** 'html' when the caller renders formatted replies (with `<button data-action="N">` for in-text actions); otherwise the reply is plain text. */
  format?: 'html';
}

export interface ConsoleAssistantResponse {
  /**
   * With `format: 'html'`: simple HTML (9 October 2026), only p, strong, em, ul, ol, li and br with no attributes, plus
   * `<button data-action="N">` for action N (from 1) placed inside the text. Render from an allow-list, never as raw
   * HTML. Without it: plain text.
   */
  reply: string;
  actions: ConsoleAssistantAction[];
  in_tokens: number;
  out_tokens: number;
}

export function askConsoleAssistant(req: ConsoleAssistantRequest): Promise<Envelope<ConsoleAssistantResponse>> {
  return http.post<Envelope<ConsoleAssistantResponse>>('/v1/console/assistant', { body: req });
}
